// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, Lock, ShieldCheck } from 'lucide-react'
import { Logo } from '@/components/Logo'
import { Branding } from '@qh/design-tokens/branding'
import { exitKioskMode, clearKioskSession, getKioskReturnUrl, returnToUrl } from '@/lib/kiosk-mode'
import { getPatientFirstName } from '@/lib/session'

export function KioskHandbackScreen() {
  const { t } = useTranslation()
  const [unlockOpen, setUnlockOpen] = useState(false)
  const firstName = getPatientFirstName() ?? ''

  function handleStaffTap() {
    setUnlockOpen(true)
  }

  return (
    <div className="min-h-dvh flex flex-col bg-gradient-to-br from-[#011829] via-[#012540] to-[#011829] relative overflow-hidden">
      {/* Ambient glows */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-10%] right-[-5%] w-[450px] h-[450px] rounded-full bg-emerald-500/8 blur-[140px]" />
        <div className="absolute bottom-[10%] left-[-10%] w-[350px] h-[350px] rounded-full bg-[#02C7C3]/6 blur-[120px]" />
      </div>

      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-6 pt-10 pb-2 sm:px-10">
        <div style={{ filter: 'brightness(0) invert(1)' }}>
          <Logo variant="mark" className="h-7 w-auto" aria-hidden />
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/30">
          <CheckCircle2 className="h-3 w-3 text-emerald-400" />
          <span className="text-[11px] font-semibold text-emerald-400 uppercase tracking-widest">
            {t('kiosk.submittedBadge')}
          </span>
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col flex-1 items-center justify-center px-6 py-10 sm:px-10">
        <div className="w-full max-w-sm flex flex-col items-center text-center">

          {/* Success icon */}
          <div className="relative mb-8">
            <div className="absolute inset-0 rounded-full bg-emerald-500/20 blur-2xl scale-[1.8]" />
            <div className="relative h-24 w-24 rounded-3xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-2xl shadow-emerald-500/30">
              <CheckCircle2 className="h-12 w-12 text-white" />
            </div>
          </div>

          {/* Heading */}
          <h1 className="text-[36px] sm:text-4xl font-bold text-white leading-tight mb-3">
            {firstName ? t('kiosk.doneGreetingName', { name: firstName }) : t('kiosk.doneGreeting')}
          </h1>

          {/* Sent message */}
          <p className="text-white/70 text-[16px] leading-relaxed mb-2">
            {t('kiosk.doneSent')}
          </p>
          <p className="text-white/50 text-[15px] leading-relaxed mb-6">
            {t('kiosk.doneHandback')}
          </p>

          {/* Footer note */}
          <p className="text-white/35 text-[13px] leading-relaxed">
            {t('kiosk.doneNote')}
          </p>
        </div>
      </div>

      {/* Staff unlock affordance — small and deliberate at bottom */}
      <div className="relative z-10 flex flex-col items-center gap-3 pb-6 px-6">
        <button
          onClick={handleStaffTap}
          className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-white/25 text-xs font-medium hover:border-white/20 hover:text-white/40 transition-colors"
        >
          <Lock className="h-3 w-3" />
          {t('kiosk.staffUnlock')}
        </button>
        <Branding variant="inverse" />
      </div>

      {/* PIN unlock overlay */}
      {unlockOpen && <PinUnlockOverlay onClose={() => setUnlockOpen(false)} />}
    </div>
  )
}

function PinUnlockOverlay({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [pin, setPin] = useState(['', '', '', ''])
  const [unlocked, setUnlocked] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([null, null, null, null])
  // Capture the provider return target BEFORE clearing the session (clear wipes it).
  const returnUrl = useRef(getKioskReturnUrl()).current

  // Staff done → go back to the provider-admin page that launched the kiosk
  // (a different origin, so a full navigation — not the SPA router). Falls back
  // to the patient home if this tablet was opened without a return target.
  function leaveKiosk() {
    if (returnUrl) returnToUrl(returnUrl)
    else navigate('/', { replace: true })
  }

  function handlePinChange(index: number, value: string) {
    if (!/^\d?$/.test(value)) return
    const next = [...pin]
    next[index] = value
    setPin(next)
    if (value && index < 3) inputRefs.current[index + 1]?.focus()
  }

  function handlePinKeyDown(index: number, e: React.KeyboardEvent) {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  // Pilot: any 4-digit code unlocks. Real backend will gate via
  // POST /v1/kiosk/:token/unlock with 3-attempt limit + cooldown.
  function handleUnlock() {
    if (pin.join('').length < 4) return
    clearKioskSession()
    exitKioskMode()
    setUnlocked(true)
  }

  if (unlocked) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#011829]/95 backdrop-blur-sm px-6">
        <div className="flex flex-col items-center text-center gap-5 max-w-xs">
          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-[#006EF9] to-[#02C7C3] flex items-center justify-center shadow-lg shadow-primary/30">
            <ShieldCheck className="h-8 w-8 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">{t('kiosk.unlockedTitle')}</h2>
            <p className="text-white/55 text-sm mt-1.5 leading-relaxed">{t('kiosk.unlockedBody')}</p>
          </div>
          <button
            onClick={leaveKiosk}
            className="w-full h-12 rounded-2xl font-semibold text-sm text-white bg-gradient-to-r from-[#006EF9] to-[#02C7C3] hover:opacity-90 transition-opacity"
          >
            {t('kiosk.returnHome')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#011829]/95 backdrop-blur-sm px-6">
      <div className="w-full max-w-xs flex flex-col items-center text-center gap-6">

        {/* Lock icon */}
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-[#006EF9]/20 blur-2xl scale-[1.6]" />
          <div className="relative h-16 w-16 rounded-2xl bg-white/8 border border-white/15 flex items-center justify-center">
            <Lock className="h-7 w-7 text-[#02C7C3]" />
          </div>
        </div>

        <div>
          <h2 className="text-xl font-bold text-white">{t('kiosk.pinTitle')}</h2>
          <p className="text-white/45 text-sm mt-1">{t('kiosk.pinSubtitle')}</p>
        </div>

        {/* PIN boxes */}
        <div className="flex gap-3">
          {pin.map((digit, i) => (
            <input
              key={i}
              ref={el => { inputRefs.current[i] = el }}
              type="password"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={e => handlePinChange(i, e.target.value)}
              onKeyDown={e => handlePinKeyDown(i, e)}
              autoFocus={i === 0}
              className={[
                'w-14 h-14 rounded-xl text-center text-xl font-bold text-white',
                'bg-white/8 border outline-none focus:ring-2 transition-colors',
                digit
                  ? 'border-[#02C7C3]/60 focus:ring-[#02C7C3]/20'
                  : 'border-white/15 focus:border-[#02C7C3]/50 focus:ring-[#02C7C3]/15',
              ].join(' ')}
            />
          ))}
        </div>

        {/* Unlock button */}
        <button
          onClick={handleUnlock}
          disabled={pin.some(d => !d)}
          className={[
            'w-full h-12 rounded-2xl font-semibold text-sm text-white transition-opacity',
            'bg-gradient-to-r from-[#006EF9] to-[#02C7C3]',
            pin.every(d => d) ? 'hover:opacity-90 active:opacity-80' : 'opacity-30 cursor-not-allowed',
          ].join(' ')}
        >
          {t('kiosk.pinUnlockButton')}
        </button>

        {/* Cancel + master unlock */}
        <div className="flex flex-col items-center gap-2">
          <button
            onClick={onClose}
            className="text-white/30 text-xs hover:text-white/50 transition-colors"
          >
            {t('kiosk.pinCancel')}
          </button>
          <button className="text-white/20 text-xs hover:text-white/35 transition-colors">
            {t('kiosk.masterUnlock')}
          </button>
        </div>
      </div>
    </div>
  )
}
